import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle, Send, CheckCircle, ArrowLeft, Search,
  Loader2, Package, Clock, Check, CheckCheck, User,
  Smile, X, Plus, FileText, Camera, Tag
} from "lucide-react";
import { toast } from "sonner";
// [PROPOSITION-1] Import proposition components
import PropositionModal from "../components/chat/PropositionModal";
import { OwnerPropositionCard } from "../components/chat/PropositionCard";

/* ─── Helpers ─── */
function dateSeparator(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (msgDay.getTime() === today.getTime()) return "Aujourd'hui";
  if (msgDay.getTime() === yesterday.getTime()) return "Hier";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}
function isSameDay(a, b) {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}
function timeStr(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}
function relativeTime(ts) {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "maintenant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return timeStr(ts);
}

/* ─── ConvItem ─── */
function ConvItem({ conv, isActive, onSelect, accent }) {
  const initials = (conv.clientePrenom || "?").substring(0, 2).toUpperCase();
  const preview = conv.dernierMessage?.contenu || "Nouvelle conversation";
  const isProduct = preview.startsWith("Je suis int");
  const unread = conv.nonLuBoutique || 0;

  return (
    <motion.div
      data-testid={`conversation-item-${conv.id}`}
      onClick={() => onSelect(conv)}
      initial={false}
      whileHover={{ backgroundColor: isActive ? undefined : "rgba(255,255,255,0.03)" }}
      style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "14px 18px", cursor: "pointer",
        background: isActive ? "rgba(255,255,255,0.06)" : "transparent",
        borderLeft: isActive ? `3px solid ${accent}` : "3px solid transparent",
        transition: "border-color 200ms, background 150ms",
        position: "relative",
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 44, height: 44, borderRadius: 14, flexShrink: 0,
        background: `linear-gradient(135deg, ${accent}CC, ${accent}88)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: 800, color: "#fff",
        letterSpacing: 0.5, position: "relative",
      }}>
        {initials}
        {unread > 0 && (
          <span style={{
            position: "absolute", top: -3, right: -3,
            minWidth: 18, height: 18, borderRadius: 9,
            background: "#EF4444", border: "2px solid #111",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 800, color: "#fff", padding: "0 4px",
          }} />
        )}
      </div>
      {/* Info */}
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
          <span style={{
            color: unread > 0 ? "#fff" : "#E5E7EB",
            fontWeight: unread > 0 ? 700 : 500, fontSize: 14,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {conv.clientePrenom}
          </span>
          <span style={{ color: "#6B7280", fontSize: 11, flexShrink: 0, marginLeft: 8 }}>
            {relativeTime(conv.dernierMessage?.timestamp)}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isProduct && <Package size={12} style={{ color: "#6B7280", flexShrink: 0 }} />}
          <span style={{
            color: unread > 0 ? "#D1D5DB" : "#6B7280",
            fontSize: 13, fontWeight: unread > 0 ? 500 : 400,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
          }}>
            {conv.dernierMessage?.expediteur === "boutique" && "Vous: "}{preview}
          </span>
          {unread > 0 && (
            <span style={{
              minWidth: 20, height: 20, borderRadius: 10,
              background: accent, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 800, color: "#fff", flexShrink: 0, padding: "0 5px",
            }}>
              {unread}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ─── ChatDetail ─── */
function ChatDetail({ conv, userId, onBack, onRefresh, devise, accent }) {
  const { getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [confirmQty, setConfirmQty] = useState(1);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // [PROPOSITION-1] States
  const [propositionOpen, setPropositionOpen] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevCount = useRef(0);
  const scrollRef = useRef(null);
  const plusMenuRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/conversations/${userId}/${conv.id}`, getAuthHeaders());
      const msgs = res.data.messages || [];
      setMessages(msgs);
      if (msgs.length > prevCount.current) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
      }
      prevCount.current = msgs.length;
    } catch { /* silent */ }
  }, [conv.id, userId, getAuthHeaders]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  useEffect(() => {
    const iv = setInterval(fetchDetail, 4000);
    return () => clearInterval(iv);
  }, [fetchDetail]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${userId}/${conv.id}/message`, {
        expediteur: "boutique", type: "texte", contenu: newMsg.trim()
      }, getAuthHeaders());
      setNewMsg("");
      fetchDetail();
      onRefresh();
      inputRef.current?.focus();
    } catch { toast.error("Erreur d'envoi"); }
    finally { setSending(false); }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); }
  };

  const handleConfirm = async () => {
    if (!confirming) return;
    setConfirmLoading(true);
    try {
      await axios.put(`${API}/conversations/${userId}/${conv.id}/confirmer`, {
        produitId: confirming.id, taille: confirming.taille,
        quantite: confirmQty, prix: confirming.prix
      }, getAuthHeaders());
      toast.success("Commande confirmee !");
      setConfirming(null); setConfirmQty(1);
      fetchDetail(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setConfirmLoading(false); }
  };

  const initials = (conv.clientePrenom || "?").substring(0, 2).toUpperCase();

  // [PROPOSITION-1] + [PROPOSITION-2] Confirm proposition handler with modePaiement
  const handleConfirmProposition = async (propId, modePaiement) => {
    try {
      await axios.put(`${API}/conversations/${userId}/${conv.id}/proposition/${propId}/confirmer`,
        { modePaiement: modePaiement || "en_attente" }, getAuthHeaders());
      fetchDetail(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  // [PROPOSITION-1] Close plus menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(e.target)) setPlusMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ─── Render messages ─── */
  const renderMessages = () => {
    const els = [];
    let lastDate = null;
    messages.forEach((msg, idx) => {
      if (!isSameDay(lastDate, msg.timestamp)) {
        els.push(
          <div key={`sep-${idx}`} style={{
            display: "flex", alignItems: "center", gap: 16, margin: "20px 0", padding: "0 8px",
          }}>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
            <span style={{
              color: "#6B7280", fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
              textTransform: "uppercase", whiteSpace: "nowrap",
              background: "#0A0A0A", padding: "0 12px",
            }}>
              {dateSeparator(msg.timestamp)}
            </span>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
          </div>
        );
        lastDate = msg.timestamp;
      }

      const isStore = msg.expediteur === "boutique";
      const time = timeStr(msg.timestamp);

      /* Confirmation bubble */
      if (msg.type === "confirmation") {
        els.push(
          <motion.div key={msg.id || idx}
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
            <div data-testid="msg-confirmation" style={{
              background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)",
              borderRadius: 16, padding: "14px 20px", maxWidth: 280, textAlign: "center",
              backdropFilter: "blur(8px)",
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%", margin: "0 auto 8px",
                background: "rgba(34,197,94,0.15)", display: "flex",
                alignItems: "center", justifyContent: "center",
              }}>
                <CheckCircle size={18} style={{ color: "#22C55E" }} />
              </div>
              <div style={{ color: "#22C55E", fontWeight: 700, fontSize: 13 }}>Commande confirmee</div>
              {msg.produit && (
                <div style={{ color: "#D1D5DB", fontSize: 12, marginTop: 4 }}>
                  {msg.produit.nom}{msg.produit.taille ? ` - ${msg.produit.taille}` : ""}
                </div>
              )}
              <div style={{ color: "#6B7280", fontSize: 10, marginTop: 8 }}>{time}</div>
            </div>
          </motion.div>
        );
        return;
      }

      // [PROPOSITION-1] Proposition bubble — owner side
      if (msg.type === "proposition" && msg.proposition) {
        els.push(
          <OwnerPropositionCard
            key={msg.id || idx}
            msg={msg}
            conv={conv}
            accent={accent}
            devise={devise}
            onConfirm={handleConfirmProposition}
            onNewProposition={() => { setPropositionOpen(true); }}
          />
        );
        return;
      }

      /* Product bubble */
      if (msg.type === "produit" && msg.produit) {
        els.push(
          <motion.div key={msg.id || idx}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            style={{ display: "flex", justifyContent: isStore ? "flex-end" : "flex-start", margin: "8px 0" }}>
            <div data-testid="msg-produit" style={{
              maxWidth: 300, borderRadius: 18, overflow: "hidden",
              background: "#161616", border: "1px solid rgba(255,255,255,0.06)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
            }}>
              {msg.produit.image ? (
                <div style={{ position: "relative", overflow: "hidden" }}>
                  <img src={msg.produit.image} alt={msg.produit.nom}
                    style={{ width: "100%", height: 160, objectFit: "cover" }} />
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0, height: 40,
                    background: "linear-gradient(transparent, #161616)",
                  }} />
                </div>
              ) : (
                <div style={{
                  width: "100%", height: 80,
                  background: `linear-gradient(135deg, ${accent}44, ${accent}22)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Package size={28} style={{ color: accent, opacity: 0.7 }} />
                </div>
              )}
              <div style={{ padding: "12px 16px" }}>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                  {msg.produit.nom}
                </div>
                {msg.produit.taille && (
                  <span style={{
                    display: "inline-block", background: "rgba(255,255,255,0.06)",
                    borderRadius: 6, padding: "2px 8px", fontSize: 11, color: "#9CA3AF",
                    marginBottom: 6, marginTop: 2,
                  }}>
                    Taille {msg.produit.taille}
                  </span>
                )}
                <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 4 }}>
                  <span style={{ color: "#F97316", fontWeight: 800, fontSize: 16 }}>
                    {msg.produit.prix?.toLocaleString("fr-FR")}
                  </span>
                  <span style={{ color: "#F97316", fontSize: 12 }}>{msg.produit.devise || devise}</span>
                </div>
                {!isStore && (
                  <button data-testid={`confirm-btn-${msg.id}`}
                    onClick={() => setConfirming(msg.produit)}
                    style={{
                      marginTop: 10, width: "100%", padding: "9px 0", borderRadius: 10,
                      background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)",
                      color: "#22C55E", fontWeight: 700, fontSize: 13,
                      cursor: "pointer", transition: "all 150ms",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(34,197,94,0.18)"}
                    onMouseLeave={e => e.currentTarget.style.background = "rgba(34,197,94,0.1)"}
                  >
                    <CheckCircle size={14} /> Confirmer la commande
                  </button>
                )}
                <div style={{ color: "#4B5563", fontSize: 10, marginTop: 8, textAlign: "right" }}>{time}</div>
              </div>
            </div>
          </motion.div>
        );
        return;
      }

      /* Text bubble */
      els.push(
        <motion.div key={msg.id || idx} data-testid={`msg-${msg.expediteur}`}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          style={{
            display: "flex", justifyContent: isStore ? "flex-end" : "flex-start",
            margin: "3px 0", padding: "0 4px",
          }}>
          <div style={{
            maxWidth: "68%", padding: "10px 16px",
            background: isStore
              ? `linear-gradient(135deg, ${accent}, ${accent}DD)`
              : "rgba(255,255,255,0.06)",
            borderRadius: isStore ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
            boxShadow: isStore ? `0 2px 8px ${accent}33` : "none",
          }}>
            <div style={{ color: "#fff", fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {msg.contenu}
            </div>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: isStore ? "flex-end" : "flex-start",
              gap: 4, marginTop: 4,
            }}>
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 10 }}>{time}</span>
              {isStore && msg.lu && <CheckCheck size={12} style={{ color: "rgba(255,255,255,0.5)" }} />}
              {isStore && !msg.lu && <Check size={12} style={{ color: "rgba(255,255,255,0.3)" }} />}
            </div>
          </div>
        </motion.div>
      );
    });
    return els;
  };

  return (
    <div data-testid="chat-detail" style={{ display: "flex", flexDirection: "column", height: "100%", background: "#0A0A0A" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(17,17,17,0.85)", backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}>
        <button data-testid="chat-back-btn" onClick={onBack}
          className="chat-back-mobile"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#9CA3AF", display: "none", padding: 4,
          }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: `linear-gradient(135deg, ${accent}CC, ${accent}88)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 800, color: "#fff",
        }}>
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{conv.clientePrenom}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {conv.clienteContact && (
              <span style={{ color: "#6B7280", fontSize: 12 }}>{conv.clienteContact}</span>
            )}
            {conv.produitsDiscutes && (
              <span style={{
                color: "#4B5563", fontSize: 11, display: "flex", alignItems: "center", gap: 3,
              }}>
                <Package size={10} /> {conv.produitsDiscutes.length} produit(s)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{
        flex: 1, overflow: "auto", padding: "16px 20px",
        background: "#0A0A0A",
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <MessageCircle size={36} style={{ color: "#333", marginBottom: 8 }} />
            <p style={{ color: "#4B5563", fontSize: 13 }}>Debut de la conversation</p>
          </div>
        )}
        {renderMessages()}
        <div ref={messagesEndRef} />
      </div>

      {/* Confirm modal */}
      <AnimatePresence>
        {confirming && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirming(null)}
            style={{
              position: "fixed", inset: 0, zIndex: 999,
              background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
              display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
            }}>
            <motion.div initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              transition={{ type: "spring", damping: 24, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: "#161616", borderRadius: 20, padding: 28,
                width: "100%", maxWidth: 380,
                border: "1px solid rgba(255,255,255,0.06)",
                boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
              }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <h3 style={{ color: "#fff", fontSize: 17, fontWeight: 700 }}>
                  Confirmer la commande
                </h3>
                <button onClick={() => setConfirming(null)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", padding: 4 }}>
                  <X size={18} />
                </button>
              </div>
              <div style={{
                background: "rgba(255,255,255,0.03)", borderRadius: 12, padding: 16, marginBottom: 20,
                border: "1px solid rgba(255,255,255,0.04)",
              }}>
                <div style={{ color: "#fff", fontSize: 15, fontWeight: 600 }}>{confirming.nom}</div>
                {confirming.taille && <div style={{ color: "#9CA3AF", fontSize: 13, marginTop: 2 }}>Taille: {confirming.taille}</div>}
                <div style={{ color: "#F97316", fontWeight: 800, fontSize: 18, marginTop: 8 }}>
                  {confirming.prix?.toLocaleString("fr-FR")} {confirming.devise || devise}
                </div>
                <div style={{ color: "#6B7280", fontSize: 12, marginTop: 4 }}>
                  Pour: <span style={{ color: "#D1D5DB" }}>{conv.clientePrenom}</span>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: "#9CA3AF", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Quantite
                </label>
                <input type="number" value={confirmQty} min={1}
                  onChange={e => setConfirmQty(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{
                    width: "100%", background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 10, padding: "10px 14px", color: "#fff", fontSize: 15,
                    outline: "none", transition: "border-color 200ms",
                  }}
                  onFocus={e => e.target.style.borderColor = accent}
                  onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"}
                />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setConfirming(null)}
                  style={{
                    flex: 1, padding: 12, borderRadius: 12,
                    background: "rgba(255,255,255,0.05)",
                    color: "#9CA3AF", border: "1px solid rgba(255,255,255,0.06)",
                    cursor: "pointer", fontSize: 14, fontWeight: 600, transition: "all 150ms",
                  }}>
                  Annuler
                </button>
                <button data-testid="confirm-order-submit" onClick={handleConfirm} disabled={confirmLoading}
                  style={{
                    flex: 1.2, padding: 12, borderRadius: 12,
                    background: "linear-gradient(135deg, #22C55E, #16A34A)",
                    color: "#fff", border: "none", cursor: "pointer",
                    fontSize: 14, fontWeight: 700, transition: "all 150ms",
                    opacity: confirmLoading ? 0.6 : 1,
                    boxShadow: "0 4px 12px rgba(34,197,94,0.3)",
                  }}>
                  {confirmLoading ? <Loader2 size={16} className="animate-spin" style={{ margin: "0 auto" }} /> : "Confirmer"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input */}
      <form onSubmit={handleSend} style={{
        display: "flex", gap: 10, alignItems: "flex-end",
        padding: "14px 20px", borderTop: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(17,17,17,0.85)", backdropFilter: "blur(16px)",
      }}>
        {/* [PROPOSITION-1] Plus button with context menu */}
        <div ref={plusMenuRef} style={{ position: "relative", flexShrink: 0 }}>
          <button
            type="button"
            data-testid="plus-menu-btn"
            onClick={() => setPlusMenuOpen(v => !v)}
            style={{
              width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
              background: "#1A1A1A", border: "1px solid #2A2A2A",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "all 200ms",
              transform: plusMenuOpen ? "rotate(45deg)" : "none",
            }}
          >
            <Plus size={16} color="#D1D5DB" />
          </button>
          <AnimatePresence>
            {plusMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.95 }} transition={{ duration: 0.12 }}
                data-testid="plus-menu-popup"
                style={{
                  position: "absolute", bottom: 46, left: 0,
                  background: "#1A1A1A", border: "1px solid #2A2A2A",
                  borderRadius: 12, padding: 8, minWidth: 200,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                  zIndex: 50,
                }}
              >
                <button
                  data-testid="menu-create-proposition"
                  onClick={() => { setPlusMenuOpen(false); setPropositionOpen(true); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%", padding: "10px 12px", borderRadius: 8,
                    background: "transparent", border: "none", color: "#D1D5DB",
                    fontSize: 13, fontWeight: 500, cursor: "pointer",
                    transition: "background 150ms", textAlign: "left",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <Tag size={15} style={{ color: accent }} /> Créer une proposition
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div style={{
          flex: 1, display: "flex", alignItems: "flex-end",
          background: "rgba(255,255,255,0.04)", borderRadius: 22,
          border: "1px solid rgba(255,255,255,0.06)",
          transition: "border-color 200ms", padding: "4px 6px 4px 18px",
        }}>
          <textarea
            ref={inputRef}
            data-testid="owner-message-input"
            value={newMsg}
            onChange={e => {
              setNewMsg(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
            }}
            onKeyDown={handleKeyDown}
            placeholder="Ecrire un message..."
            rows={1}
            style={{
              flex: 1, background: "none", border: "none",
              padding: "10px 0", color: "#fff", fontSize: 14,
              outline: "none", resize: "none", maxHeight: 120, lineHeight: 1.45,
              fontFamily: "inherit",
            }}
          />
        </div>
        <button type="submit" disabled={!newMsg.trim() || sending} data-testid="owner-send-btn"
          style={{
            width: 42, height: 42, borderRadius: 14, flexShrink: 0,
            background: newMsg.trim()
              ? `linear-gradient(135deg, ${accent}, ${accent}CC)`
              : "rgba(255,255,255,0.04)",
            border: "none", cursor: newMsg.trim() ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 250ms",
            boxShadow: newMsg.trim() ? `0 4px 12px ${accent}44` : "none",
            transform: newMsg.trim() ? "scale(1)" : "scale(0.95)",
          }}>
          {sending
            ? <Loader2 size={16} className="animate-spin" color="#fff" />
            : <Send size={16} color="#fff" style={{ marginLeft: 1 }} />
          }
        </button>
      </form>

      {/* [PROPOSITION-1] Proposition modal */}
      <PropositionModal
        open={propositionOpen}
        onClose={() => setPropositionOpen(false)}
        userId={userId}
        convId={conv.id}
        produitsDiscutes={conv.produitsDiscutes}
        pendingProductIds={messages.filter(m => m.type === "proposition" && m.proposition?.statut === "en_attente").map(m => m.proposition.produit.id)}
        onCreated={() => { fetchDetail(); onRefresh(); }}
        accent={accent}
        devise={devise}
      />

      <style>{`
        @media (max-width: 767px) {
          .chat-back-mobile { display: flex !important; }
        }
      `}</style>
    </div>
  );
}

/* ─── Main Conversations Page ─── */
export default function Conversations() {
  const { user, getAuthHeaders } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || "\u20ac";
  const accent = boutiqueData.customColor || boutiqueData.colorPalette?.[0] || "#D4A853";
  const ownerId = user?.id;

  const fetchList = useCallback(async () => {
    if (!ownerId) return;
    try {
      const res = await axios.get(`${API}/conversations/${ownerId}`, getAuthHeaders());
      setConversations(res.data.conversations || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [ownerId, getAuthHeaders]);

  useEffect(() => { if (ownerId) fetchList(); }, [ownerId, fetchList]);
  useEffect(() => {
    if (!ownerId) return;
    const iv = setInterval(fetchList, 10000);
    return () => clearInterval(iv);
  }, [ownerId, fetchList]);

  const filtered = conversations.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (c.clientePrenom || "").toLowerCase().includes(q)
      || (c.clienteContact || "").toLowerCase().includes(q);
  });

  const totalUnread = conversations.reduce((s, c) => s + (c.nonLuBoutique || 0), 0);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Loader2 size={24} className="animate-spin" style={{ color: accent }} />
      </div>
    );
  }

  return (
    <div data-testid="conversations-page" style={{
      display: "flex", height: "calc(100vh - 80px)",
      background: "#0F0F0F", borderRadius: 16, overflow: "hidden",
      border: "1px solid rgba(255,255,255,0.06)",
    }}>
      {/* ── Left Panel ── */}
      <div className="conv-left-panel" style={{
        width: 380, minWidth: 280,
        borderRight: "1px solid rgba(255,255,255,0.06)",
        display: "flex", flexDirection: "column",
        background: "#111",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 18, letterSpacing: -0.3 }}>
                Messages
              </span>
              {totalUnread > 0 && (
                <span style={{
                  background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
                  color: "#fff", fontSize: 11, fontWeight: 800,
                  borderRadius: 10, padding: "3px 8px", minWidth: 22, textAlign: "center",
                }}>
                  {totalUnread}
                </span>
              )}
            </div>
            <span style={{ color: "#4B5563", fontSize: 12, fontWeight: 500 }}>
              {conversations.length} conv.
            </span>
          </div>
          {/* Search */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "rgba(255,255,255,0.04)", borderRadius: 12,
            padding: "10px 14px",
            border: searchFocused ? `1px solid ${accent}55` : "1px solid rgba(255,255,255,0.06)",
            transition: "border-color 200ms",
          }}>
            <Search size={15} style={{ color: searchFocused ? accent : "#4B5563", flexShrink: 0, transition: "color 200ms" }} />
            <input
              data-testid="conv-search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Rechercher..."
              style={{
                background: "none", border: "none", color: "#fff", fontSize: 13,
                outline: "none", flex: 1, fontFamily: "inherit",
              }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", padding: 0 }}>
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div style={{ overflow: "auto", flex: 1 }}>
          {filtered.length === 0 ? (
            <div data-testid="no-conversations" style={{ textAlign: "center", padding: "60px 24px" }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16, margin: "0 auto 14px",
                background: "rgba(255,255,255,0.03)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <MessageCircle size={28} style={{ color: "#333" }} />
              </div>
              <p style={{ color: "#6B7280", fontSize: 14, fontWeight: 500 }}>
                {searchQuery ? "Aucun resultat" : "Aucune conversation"}
              </p>
              {!searchQuery && (
                <p style={{ color: "#4B5563", fontSize: 12, marginTop: 6 }}>
                  Les conversations apparaitront ici quand des clientes vous contacteront via la vitrine.
                </p>
              )}
            </div>
          ) : (
            filtered.map(conv => (
              <ConvItem key={conv.id} conv={conv}
                isActive={selected?.id === conv.id}
                onSelect={setSelected} accent={accent}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div className="conv-right-panel" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {selected ? (
          <ChatDetail
            conv={selected} userId={ownerId}
            onBack={() => setSelected(null)} onRefresh={fetchList}
            devise={devise} accent={accent}
          />
        ) : (
          <div style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
            flexDirection: "column", gap: 14, background: "#0A0A0A",
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: 22,
              background: "rgba(255,255,255,0.03)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <MessageCircle size={32} style={{ color: "#333" }} />
            </div>
            <p style={{ color: "#4B5563", fontSize: 15, fontWeight: 500 }}>
              Selectionnez une conversation
            </p>
            <p style={{ color: "#333", fontSize: 13 }}>
              Choisissez un fil de discussion pour commencer
            </p>
          </div>
        )}
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 767px) {
          .conv-left-panel { width: 100% !important; display: ${selected ? "none" : "flex"} !important; }
          .conv-right-panel { display: ${selected ? "flex" : "none"} !important; }
        }
      `}</style>
    </div>
  );
}
